import { get, writable } from 'svelte/store';
import { deviceRuntimeDiagnostics } from 'src/stores/features/dev/device-runtime-diagnostics';

export type DeviceRuntimePhase = 'idle' | 'loading' | 'ready' | 'error';

export interface DeviceRuntimeResourceState {
	phase: DeviceRuntimePhase;
	generation: number;
	error?: unknown;
	updatedAt?: number;
}

const initialState: DeviceRuntimeResourceState = {
	phase: 'idle',
	generation: 0,
};

const store = writable<DeviceRuntimeResourceState>(initialState);
const loadStartedAt = new Map<number, number>();

export const deviceRuntimeResource = {
	subscribe: store.subscribe,
	begin(): number {
		const generation = get(store).generation + 1;
		store.update((state) => ({
			...state,
			phase: 'loading',
			generation,
			error: undefined,
		}));
		loadStartedAt.clear();
		loadStartedAt.set(generation, Date.now());
		deviceRuntimeDiagnostics.record({ type: 'load:start', generation });
		return generation;
	},
	isCurrent(generation: number): boolean {
		return get(store).generation === generation;
	},
	complete(generation: number): boolean {
		if (get(store).generation !== generation) return false;
		const startedAt = loadStartedAt.get(generation);
		store.update((state) => ({
			...state,
			phase: 'ready',
			error: undefined,
			updatedAt: Date.now(),
		}));
		loadStartedAt.delete(generation);
		deviceRuntimeDiagnostics.record({
			type: 'load:complete',
			generation,
			durationMs: startedAt ? Date.now() - startedAt : undefined,
		});
		return true;
	},
	fail(generation: number, error: unknown): boolean {
		if (get(store).generation !== generation) return false;
		const startedAt = loadStartedAt.get(generation);
		store.update((state) => ({
			...state,
			phase: 'error',
			error,
		}));
		loadStartedAt.delete(generation);
		deviceRuntimeDiagnostics.record({
			type: 'load:error',
			generation,
			durationMs: startedAt ? Date.now() - startedAt : undefined,
			details: {
				errorType: error instanceof Error ? error.name : typeof error,
			},
		});
		return true;
	},
	reset(): void {
		const generation = get(store).generation + 1;
		loadStartedAt.clear();
		store.set({ ...initialState, generation });
		deviceRuntimeDiagnostics.record({ type: 'runtime:reset', generation });
	},
};
